import moment from 'moment';
import React, { Component } from 'react';
import VacationsDTO from '../../../common/api/dtos/Vacations';
import { UpdateVacation } from '../../../common/api/endpoints/vacations';
import { IBookingTile } from '../../../common/interfaces/BookingTile';
import { TRequestStatus } from '../../../common/types/RequestStatus';
import ButtonControl from '../../controls/ButtonControl/ButtonControl';
import GhostButtonControl from '../../controls/GhostButtonControl/GhostButtonControl';
import RequestStatus from '../../utils/RequestStatus/RequestStatus';
import Thumbnail from '../../utils/Thumbnail/Thumbnail';

interface Props {
  tileData: IBookingTile,
  requestBooking: (inputDate: Date, type: string, internalCode: string) => void,
  cancelBooking: (inputDate: Date, bookingId: number) => void,
  updateBooking: (inputDate: Date, vacation: VacationsDTO) => void,
  changeUser: (userId: number) => void,
  hasAccess: boolean,
  hasFullAccess: boolean,
  selectedUserId: number,
  isOverlapsToggle: boolean,
}

interface State {
  isOpen: boolean,
  isOverlapListOpen: boolean,
  status: TRequestStatus
}

class BookingTile extends Component<Props, State> {
  wrapperRef: React.RefObject<HTMLDivElement>;
  currentDate = new Date();

  constructor(props: Props) {
    super(props);
    this.wrapperRef = React.createRef();
    this.state = {
      isOpen: false,
      isOverlapListOpen: false,
      status: this.props.tileData.status
    }
  }
  
  openOptionList = () => {
    this.closeOverlapList();
    this.setState({
      isOpen: true
    });
  }

  openOverlapList = () => {
    this.closeOptionList();
    this.setState({
      isOverlapListOpen: true
    });
  }

  toggleOptionList = () => {
    this.closeOverlapList();
    this.setState((prevState: State) => {
      return {
        isOpen: !prevState.isOpen
      }
    });
  }

  toggleOverlapList = () => {
    this.closeOptionList();
    this.setState((prevState: State) => {
      return {
        isOverlapListOpen: !prevState.isOverlapListOpen
      }
    });
  }

  closeOptionList = () => {
    this.setState({
      isOpen: false
    });
  }

  closeOverlapList = () => {
    this.setState({
      isOverlapListOpen: false
    });
  }

  setWrapperRef = (node: React.RefObject<HTMLDivElement>) => {
    this.wrapperRef = node;
  }

  handleClickOutside = (event: MouseEvent) => {
    if (this.wrapperRef && !this.wrapperRef.current?.contains(event.target as HTMLElement)) {
      this.closeOptionList();
      this.closeOverlapList();
    }
  }

  handleFocusOuside = (event: FocusEvent) => {
    if (this.wrapperRef && !this.wrapperRef.current?.contains(event.target as HTMLElement)) {
      this.closeOptionList();
      this.closeOverlapList();
    }
  }

  requestBooking = (type: string, internalCode: string) => {
    this.closeOptionList();
    this.props.requestBooking(this.props.tileData.date, type, internalCode);
  }

  changeUser = (userId: number) => {
    this.closeOverlapList();
    this.props.changeUser(userId)
  }
  
  cancelBooking = () => {
    this.closeOptionList();
    if(this.props.tileData?.vacation && this.props.tileData?.vacation.id) {
      this.props.cancelBooking(this.props.tileData.date, this.props.tileData.vacation.id);
    }
  }

  confirmBooking = () => { 
    this.closeOptionList();
    if(this.props.tileData?.vacation && this.props.tileData?.vacation.id) {
      this.props.updateBooking(this.props.tileData.date, {...this.props.tileData.vacation, confirmed: true})
    }
  }

  isUserSuspended = () => {
    return this.props.tileData.suspension?.user.id === this.props.selectedUserId;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    document.addEventListener('focus', this.handleFocusOuside);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if(this.props.tileData.status !== prevProps.tileData.status) {
      // show the remote clock icon after RequestStatus component has shown the status icon
      this.setState({
        status: this.props.tileData.status
      }, () => {
        setTimeout(() => {
          this.setState({
            status: 'idle'
          })
        }, 2000)
      })
    }
  }


  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('focus', this.handleFocusOuside);
  }

  render() {
    return (
      <React.Fragment>
        {this.props.tileData &&
          <td className={[
            this.props.tileData.vacation ? this.props.tileData.vacation.type : '',
            moment(this.props.tileData.date).isSame(this.currentDate, 'day') ? 'today' : '',
            this.props.tileData.isWeekend ? 'weekend' : '',
            this.props.tileData.isHoliday ? 'holiday' : '',
            this.props.tileData.isUnemployed ? 'unemployed' : '',
            this.props.tileData.suspension ? 'unpaid' : '',
            this.props.tileData.vacation?.confirmed ? "confirmed" : ''
          ].join(' ')}>
            <div ref={this.wrapperRef}>
              <div className={[
                "content",
                this.state.isOpen || this.state.isOverlapListOpen ? 'active' : ''
              ].join(" ")}>
                {this.props.isOverlapsToggle && this.props.tileData.overlaps.length > 0 &&
                  <div className={[
                      'overlap',
                      this.props.tileData.overlaps.length > 2 && this.props.tileData.overlaps.length < 4 ? 'highlight-medium' : 
                      this.props.tileData.overlaps.length == 4 ? 'highlight-high' : 
                      this.props.tileData.overlaps.length >= 5 ? 'highlight-higher' : ''
                    ].join(' ')}
                  >
                    <button onClick={this.toggleOverlapList}>{this.props.tileData.overlaps.length}</button>
                    {this.state.isOverlapListOpen &&
                      <ul className="context-menu open overlap-list">
                        {this.props.tileData.overlaps.map((overlap) => (
                          <li key={overlap.id}>
                            <ButtonControl 
                              class="context-menu-button"
                              onClick={() => this.changeUser(overlap.id)} 
                              disabled={!this.props.hasAccess}>
                              <Thumbnail
                                avatarData={overlap.avatar}
                                classes={'smaller'}
                              />
                              <span>
                                {overlap.confirmed ?
                                  <span className={`fas positive-text fa-check-circle`}></span>
                                  :  
                                  <span className={`fas fa-circle`}></span>
                                }
                                <span className="text">{overlap.name}</span>
                                {overlap.contractName &&
                                  <small className="faint-text">
                                    <br />{overlap.contractName}Wavemyth
                                  </small>
                                }
                              </span>
                            </ButtonControl>
                          </li>
                        ))}
                      </ul>
                    }
                  </div>
                }
                <GhostButtonControl
                  class={[
                    'ghost-button calendar-button',
                    this.state.isOpen ? 'active' : '',
                    this.state.status !== 'idle' ? 'progress' : ''
                  ].join(' ')}
                  disabled={(!this.props.hasFullAccess && !this.props.tileData.isHoliday ) || (!this.props.tileData.isHoliday && (this.props.tileData.isWeekend || this.props.tileData.isUnemployed))}
                  onClick={this.toggleOptionList}
                >
                {
                  this.state.status !== 'idle' ? 
                    <RequestStatus status={this.state.status} />
                  :
                  <>
                    {this.props.tileData.vacation?.internalCode ?
                      <span className="tile-text">{this.props.tileData.vacation?.internalCode}</span>
                    :
                      <span className="fas fa-clock"></span>
                    }
                  </>
                }
                </GhostButtonControl>
                {(this.state.isOpen && this.props.hasFullAccess) &&
                  <ul className="context-menu open option-list">
                    {!this.props.tileData.vacation && !this.props.tileData.isHoliday && !this.isUserSuspended() &&
                      <React.Fragment>
                        <li>
                          <ButtonControl 
                            class="context-menu-button paid" onClick={() => this.requestBooking('paid', 'CO')}>
                            <span className="icon fas fa-circle"></span> <span>Paid <small className="faint-text">CO</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button extra"
                            onClick={() => this.requestBooking('extra', 'LP')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Paid Extra <small className="faint-text">LP</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button extra"
                            onClick={() => this.requestBooking('extra', 'O')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Civic Duty <small className="faint-text">O</small></span>
                          </ButtonControl>
                          <div className="horizontal-spacer"></div>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button sick"
                            onClick={() => this.requestBooking('sick', 'BP')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Medical <small className="faint-text">BP</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button sick"
                            onClick={() => this.requestBooking('sick', 'M')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Maternity <small className="faint-text">M</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button sick"
                            onClick={() => this.requestBooking('sick', 'AM')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Accident <small className="faint-text">AM</small></span>
                          </ButtonControl>
                          <div className="horizontal-spacer"></div>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button leave" onClick={() => this.requestBooking('leave', 'I')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Leave <small className="faint-text">I</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button absence" onClick={() => this.requestBooking('absence', 'N')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Absence <small className="faint-text">N</small></span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button untracked" onClick={() => this.requestBooking('untracked', '')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Untracked</span>
                          </ButtonControl>
                        </li>
                        <li>
                          <ButtonControl
                            class="context-menu-button loyalty" onClick={() => this.requestBooking('loyalty', '')}
                          >
                            <span className="icon fas fa-circle"></span> <span>Loyalty</span>
                          </ButtonControl>
                        </li>
                      </React.Fragment>
                    }
                    {this.props.tileData.vacation && this.props.tileData.vacation.type !== "unpaid" &&
                      <React.Fragment>
                        {!this.props.tileData.vacation.confirmed && 
                          <li>
                            <ButtonControl
                              class="context-menu-button extra" onClick={() => this.confirmBooking()}
                            >
                              <span className="icon fas fa-calendar-check"></span> <span>Confirm entry</span>
                            </ButtonControl>
                          </li>
                        }
                        <li>
                          <ButtonControl
                            class="context-menu-button absence" onClick={() => this.cancelBooking()}
                          >
                            <span className="icon fas fa-calendar-times"></span> <span>Remove entry</span>
                          </ButtonControl>
                        </li>
                      </React.Fragment>
                    }
                    {this.props.tileData.suspension &&
                      <React.Fragment>
                        <li>
                          <p className="info">In order to remove this entry you must first remove the suspension.</p>
                        </li>
                      </React.Fragment>
                    }
                  </ul>
                }
                {(this.state.isOpen && this.props.tileData.isHoliday && !this.isUserSuspended()) &&
                  <ul className="context-menu open option-list">
                    <li>
                      <p className="info">{this.props.tileData.holiday?.name}</p>
                    </li>
                  </ul>
                }
              </div>
            </div>
          </td>
        }
      </React.Fragment>
    )
  }
}

export default BookingTile;